import { useState, useEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import DataTable from "examples/Tables/DataTable";

import toast from "react-hot-toast";
import moment from "moment";
import { capitalCase } from "capital-case";

import { AccountContext } from "context/account";
import CategoryContext from "context/category-context";

import { StoreContext } from "context/store";
import SharedContentContext from "context/shared-content";

import { compare } from "utils/helper";
import { getFileImage, getStyleImages, canResizeImageUrl } from "utils/imageHelper";

import Prompt from "components/UpStyled/Prompt";
import api from "services/api";

import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import { Empty } from "antd";

const avatarSize = 26;
const SharedStyleList = ({ size = 60, limit, tableOptions = {} }) => {
  const [styleList, setStyleList] = useState([]);
  const [filteredStyles, setFilteredStyles] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState({});

  const navigate = useNavigate();
  const owners = styleList.filter((x) => x.store).map((x) => x.store);

  const account = useContext(AccountContext);
  const { resourceList } = useContext(SharedContentContext);

  const {
    tree,
    actions: { refreshCategoryTree, findNodeByID, findParents },
  } = useContext(CategoryContext);

  useEffect(() => {
    retrieveStyles();
  }, []);

  const retrieveStyles = async () => {
    try {
      const { data } = await api.products.sharedList();

      let myProducts = data?.products || [];
      myProducts = myProducts.sort((a, b) => compare(a, b, "-updated_at"));
      if (limit) {
        myProducts = myProducts.slice(0, limit);
      }

      setStyleList(myProducts || []);
      setFilteredStyles(myProducts || []);
    } catch (err) {
      toast.error(`Retrieve style list failed`);
      console.log(err);
    }
  };

  const getStyleTableRow = (item) => {
    const styleMeta = item?.style || {};

    let category = item?.type || "N/A";
    let path = [];
    if (typeof category == "object") {
      category = category?.value;
    } else {
      const categoryNode = findNodeByID(tree, category);

      category = categoryNode?.label || category;
    }

    let styleImages = getStyleImages(item, false);
    let resource = resourceList.find((x) => x.resource_id == item.id) || {};

    return {
      key: item.id,
      image: styleImages[0],
      name: item.title,
      style_number: styleMeta?.style_number,
      size: styleMeta?.size,
      category: category,
      created_at: item.created_at,
      season: styleMeta?.season,
      year: styleMeta?.year,
      brand: styleMeta?.brand,
      updated_at: item.updated_at,
      shared_at: resource?.created_at,
      owner: item.store,
      ownerName: item.store?.name,
    };
  };

  const styleColumns = [
    {
      Header: "Image",
      accessor: "image",
      align: "center",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return (
          <Link to={`/shared-with-me/styles?id=${row?.original?.key}`}>
            <ImageCardWrapper data={value} mode={"normal"} height={size} width={size} iconMode />
          </Link>
        );
      },
    },
    {
      Header: "Style No.",
      accessor: "style_number",
      isSorted: false,
      canSort: false,
    },
    {
      Header: "Name",
      accessor: "name",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return <div style={{ maxWidth: 400 }}>{value}</div>;
      },
    },
    {
      Header: "Category",
      accessor: "category",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return <div style={{ wordBreak: "break-word", maxWidth: 80 }}>{value}</div>;
      },
    },
    {
      Header: "Season",
      accessor: "season",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return <div style={{ wordBreak: "break-word", maxWidth: 80 }}>{value?.label}</div>;
      },
    },

    {
      Header: "Updated Date",
      accessor: "updated_at",
      resizable: true,
      Cell: ({ row, value }) => {
        return (
          <Tooltip title={value ? moment(value).format("MM/DD/YY HH:mm:ss") : ""}>
            <div>{value ? moment(value).format("MM/DD/YY") : ""}</div>
          </Tooltip>
        );
      },
    },
    {
      Header: "Shared Date",
      accessor: "shared_at",
      Cell: ({ row, value }) => {
        return (
          <Tooltip title={value ? moment(value).format("MM/DD/YY HH:mm:ss") : ""}>
            <div>{value ? moment(value).format("MM/DD/YY") : ""}</div>
          </Tooltip>
        );
      },
    },
    {
      Header: "Owner",
      accessor: "ownerName",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        return (
          <SuiBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="inherit"
            sx={{ cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              if (row.original?.owner?.id) {
                account?.id
                  ? navigate(`/wall/portfolio/${row.original?.owner?.id}`)
                  : navigate(`/public/portfolio/${row.original?.owner?.id}`);
              }
            }}
          >
            <Avatar
              src={row.original?.owner?.metadata?.avatar}
              sx={{ width: avatarSize, height: avatarSize, mr: 0.5 }}
            />
            {value}
          </SuiBox>
        );
      },
    },
  ];

  const styleRows = filteredStyles.map((item) => getStyleTableRow(item));

  const handleStyleRowClick = (row) => {
    navigate(`/shared-with-me/styles?id=${row?.original?.key}`);
    //window.open(`/workspace/styles-master?id=${row?.original?.key}`, "_blank");
  };

  const handleFilterMoodboard = (owner) => {
    if (owner.id == selectedOwner.id) {
      setSelectedOwner({});
      setFilteredStyles(styleList);
    } else {
      setSelectedOwner(owner);
      const newStyles = styleList.filter((x) => x?.store?.id == owner?.id);
      setFilteredStyles(newStyles);
    }
  };

  const removeDuplicate = (storeList) => {
    let idSet = new Set();
    let filteredList = [];
    for (let i = 0; i < storeList.length; i++) {
      if (idSet.has(storeList[i].id)) {
        //
      } else {
        idSet.add(storeList[i].id);
        filteredList.push(storeList[i]);
      }
    }
    return filteredList;
  };

  const noDuplicateOwners = removeDuplicate(owners);

  const HeaderTool = (
    <SuiBox display="flex" alignItems="center" gap={0.5} overflow="auto">
      <SuiTypography sx={{ mr: 0.5, whiteSpace: "noWrap", minWidth: 100 }}>
        Filter by owner:
      </SuiTypography>
      {noDuplicateOwners.map((x) => {
        const storeInfo = x || {};
        return (
          <Tooltip title={storeInfo?.name}>
            <SuiBox
              borderRadius="50%"
              width={avatarSize + 8}
              height={avatarSize + 8}
              minWidth={avatarSize + 8}
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              border={({ borders: { borderWidth }, palette: { info } }) =>
                selectedOwner.id == x?.id ? `${borderWidth[1]} solid ${info.main}` : "none"
              }
              sx={{ cursor: "pointer" }}
            >
              <Avatar
                src={storeInfo?.metadata?.avatar}
                sx={{ width: avatarSize, height: avatarSize, cursor: "pointer" }}
                onClick={() => {
                  handleFilterMoodboard(x);
                }}
              />
            </SuiBox>
          </Tooltip>
        );
      })}
    </SuiBox>
  );

  return (
    <SuiBox>
      <SuiBox sx={{ overflow: "auto" }}>
        {styleRows.length > 0 ? (
          <DataTable
            table={{ columns: styleColumns, rows: styleRows }}
            entriesPerPage={{ defaultValue: 10 }}
            showTotalEntries={true}
            pagination={true}
            handleRowClick={handleStyleRowClick}
            isSorted
            canSearch
            HeaderTool={HeaderTool}
            {...tableOptions}
          />
        ) : (
          <Empty description={false} />
        )}
      </SuiBox>
    </SuiBox>
  );
};

export default SharedStyleList;
